import {
  GetServerSideProps,
  NextApiRequest,
  NextApiResponse,
  NextPage,
} from 'next'
import css from 'styled-jsx/css'
import cx from 'classnames'
import { useForm } from 'react-hook-form'

import FormBox from '../components/FormBox'
import Button from '../components/Button'
import Container from '../components/Container'
import Input from '../components/Input'
import Layout from '../components/Layout'
import Title from '../components/Title'
import { getLoginSession } from '../utils/auth/auth'
import { getServerSideProps as getSSRProps } from '../utils/settings'

type ForgotPasswordFormInputs = {
  email: string
}

type FormError = {
  form?: string
}

const ForgotPassword: NextPage = () => {
  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors, isValid, isSubmitSuccessful },
  } = useForm<ForgotPasswordFormInputs & FormError>({
    mode: 'onChange',
  })

  // @ts-ignore
  async function onSubmit(...args) {
    clearErrors('form')
    return handleSubmit(async (data) => {
      try {
        clearErrors('form')

        const res = await fetch('/api/forgot-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: data.email,
          }),
        })

        const jsonData = await res.json()

        if (res.status !== 200) {
          throw new Error(
            jsonData.message || jsonData.data || 'Something went wrong'
          )
        }
      } catch (error: any) {
        setError('form', {
          type: 'manual',
          message: error?.message,
        })
      }
    })(...args)
  }

  return (
    <Layout>
      <Container
        className={cx('flex flex-col', isSubmitSuccessful ? 'py-48' : 'py-32')}
      >
        {isSubmitSuccessful ? (
          <div>
            <Title
              title="Email Sent"
              className={cx('success-title', components.className)}
            />
            <p className="mt-4 text-center font-inter text-xl text-black-800">
              We sent a message to your email with a link to get back into your
              account.
            </p>
          </div>
        ) : (
          <>
            <Title title="Forgot Password" className="text-center" />
            <form onSubmit={onSubmit}>
              <FormBox>
                <Input
                  placeholder="*Email"
                  containerClass="w-full mb-6"
                  error={errors.email?.message}
                  {...register('email', {
                    required: 'This field is required',
                  })}
                />

                {errors['form']?.message && (
                  <p className="mb-6 text-base font-semibold text-red-400">
                    {errors['form'].message}
                  </p>
                )}

                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-8">
                  <Button disabled={!isValid}>Submit</Button>
                </div>
              </FormBox>
            </form>
          </>
        )}
      </Container>
      {components.styles}
    </Layout>
  )
}

const components = css.resolve`
  .success-title {
    @apply text-center;
    letter-spacing: -0.03em;
  }
`

export const getServerSideProps: GetServerSideProps = async function ({
  req,
  res,
}) {
  const session = await getLoginSession(
    req as NextApiRequest,
    res as NextApiResponse
  )
  if (session) {
    return {
      redirect: {
        permanent: false,
        destination: '/',
      },
      props: {},
    }
  }

  const pageProps = await getSSRProps(
    req as NextApiRequest,
    res as NextApiResponse
  )
  return pageProps
}
export default ForgotPassword
